import { useCallback, useEffect } from 'react'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/src/utils/sharedHooks'
import { useAnalyticsTrack } from '@/features/amplitude'

import { Text, Icon, Alert, Card, Image, Button, CardColors } from '@/ui'
import { TypographyStyles } from '@/styles'

import {
  checkEstimateIsUnpaid,
  getGroupedActiveTaxEstimatesWithinActiveQuarter,
  getGroupedTaxEstimatesForQuarter,
  selectAreAnyEstimatesLastQuarterNotPaidOrPaidAfterIRSDeadline,
  selectPreviousQuarterSafeHarborAvailable,
} from '../userTaxEstimates.selector'
import { LatePaymentRiskCallout } from './LatePaymentRiskCallout'
import { UserTaxEstimateSummary } from './UserTaxEstimateRow'

import {
  selectTaxCalculationByTaxQuarter,
  selectCalculationHomeState,
  selectUserTaxCalculationWithinActiveQuarter,
} from '@/selectors/userTaxCalculation.selectors'
import { faClose, faCalendar } from '@fortawesome/pro-regular-svg-icons'
import { faCalendarCheck } from '@fortawesome/pro-solid-svg-icons'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { UserTaxEstimate } from '../userTaxEstimates.slice'

import { HighEstimatesAlert } from '../HighEstimatesAlert'
import { LastQuarterSafeHarborFlow } from './calculate-safe-harbor-flow/LastQuarterSafeHarborFlow'
import {
  UserTaxCalculation,
  UserTaxCalculationStatuses,
  updateUserTaxCalculation,
} from '@/features/admin/user-tax-calculations/userTaxCalculation.slice'
import {
  selectActiveQTEDetailsAsDates,
  selectQuarterDetailsForQuarterBeforeActiveQuarter,
} from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.selector'

/*
  Provides a summary for the active quarter – "Q3 2022 Quarterly Taxes"
  - Displays the payment due date
  - Displays a summary for the entire group
  - Lists each individual estimate's summary
*/
const EstimatesForQuarter = ({
  federalTaxEstimate,
  homeStateTaxEstimate,
  stateTaxEstimates,
}: {
  federalTaxEstimate: UserTaxEstimate | null
  homeStateTaxEstimate: UserTaxEstimate | null
  stateTaxEstimates: UserTaxEstimate[] | null
}) => (
  <div className="w-full flex flex-col gap-4">
    {federalTaxEstimate && (
      <UserTaxEstimateSummary
        overrideBgColor={CardColors.LEVEL_1}
        estimate={federalTaxEstimate}
      />
    )}
    {homeStateTaxEstimate && (
      <UserTaxEstimateSummary
        overrideBgColor={CardColors.LEVEL_1}
        estimate={homeStateTaxEstimate}
      />
    )}
    {stateTaxEstimates?.map((e) => (
      <UserTaxEstimateSummary
        key={`state-estimate-${e.id}`}
        overrideBgColor={CardColors.LEVEL_1}
        estimate={e}
      />
    ))}
  </div>
)

const UnseenEstimateBanner = ({
  calculation,
}: {
  calculation?: UserTaxCalculation | null
}) => {
  const dispatch = useAppDispatch()
  const onClose = useCallback(async () => {
    if (calculation) {
      await dispatch(
        updateUserTaxCalculation(calculation.id, {
          unseenEstimate: false,
        })
      )
    }
  }, [calculation, dispatch])

  if (
    calculation?.status !== UserTaxCalculationStatuses.savedAndNotified ||
    calculation?.unseenEstimate === false ||
    calculation?.unseenEstimate === null
  ) {
    return null
  }

  return (
    <Card
      color={CardColors.LEVEL_1}
      className="relative border border-gray-200 mb-6"
    >
      <Button
        iconLeft={faClose}
        className="absolute top-3 right-3"
        color="darkGray"
        size="square"
        variant="discreet"
        onClick={onClose}
      />
      <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-11">
        <Image
          alt="Sparkle Paper Plane"
          className="h-[90px]"
          src="https://heard-images.s3.amazonaws.com/assets/sparkle-paper-plane.svg"
        />
        <Text variant={TypographyStyles.H3}>
          Your new federal tax estimate has arrived!
        </Text>
      </div>
    </Card>
  )
}

export const ActiveQuarterTaxSummary = ({
  quarterText: activeQuarterText,
}: {
  quarterText: string
}) => {
  const activeQuarterDetails = useReselector(selectActiveQTEDetailsAsDates)
  const calculation = useReselector(selectUserTaxCalculationWithinActiveQuarter)
  const homeState = useReselector(selectCalculationHomeState, calculation?.id)
  const { federalTaxEstimate, homeStateTaxEstimate, stateTaxEstimates } =
    useReselector(getGroupedActiveTaxEstimatesWithinActiveQuarter, homeState)

  const track = useAnalyticsTrack()

  useEffect(() => {
    const trackAttributes: {
      [key: string]: string | number | null
    } = {
      quarter: activeQuarterDetails?.taxQuarter ?? null,
      year: activeQuarterDetails?.taxYear ?? null,
      federalEstimateInCents: federalTaxEstimate?.estimateInCents ?? null,
    }

    const allStateEstimates = [
      ...(stateTaxEstimates ?? []),
      homeStateTaxEstimate,
    ]
    for (const stateEstimate of allStateEstimates ?? []) {
      if (stateEstimate) {
        trackAttributes[`stateEstimateInCents_${stateEstimate.filingState}`] =
          stateEstimate.estimateInCents
      }
    }

    track?.('viewed active quarter estimates', trackAttributes)
  }, [
    federalTaxEstimate?.estimateInCents,
    activeQuarterDetails,
    stateTaxEstimates,
    homeStateTaxEstimate,
    track,
  ])

  if (!activeQuarterDetails) {
    return null
  }

  const federalIsUnpaid = Boolean(
    federalTaxEstimate && checkEstimateIsUnpaid(federalTaxEstimate)
  )
  const stateIsUnpaid = [
    homeStateTaxEstimate,
    ...(stateTaxEstimates ?? []),
  ].some((estimate) => estimate && checkEstimateIsUnpaid(estimate))

  return (
    <div className="w-full space-y-6">
      <UnseenEstimateBanner calculation={calculation} />
      {(federalIsUnpaid || stateIsUnpaid) && (
        <LatePaymentRiskCallout
          agencies={
            federalIsUnpaid && stateIsUnpaid
              ? 'both'
              : federalIsUnpaid
                ? 'federal'
                : 'state'
          }
        />
      )}
      <Text variant={TypographyStyles.H3}>{activeQuarterText} Estimates</Text>
      <Text className="flex items-center gap-2">
        <Icon icon={faCalendar} className="text-neutral-500" />
        IRS payment due{' '}
        {activeQuarterDetails.forDisplay.irsPaymentDue.toFormat(
          DATE_FORMATS_LUXON.DISPLAY_LONG
        )}
      </Text>
      <HighEstimatesAlert
        title="This estimate seems high"
        taxCalc={calculation}
      />
      <EstimatesForQuarter
        federalTaxEstimate={federalTaxEstimate}
        homeStateTaxEstimate={homeStateTaxEstimate}
        stateTaxEstimates={stateTaxEstimates}
      />
    </div>
  )
}

const LastQuarterNoSafeHarborContent = () => {
  const activeQuarterDetails = useReselector(selectActiveQTEDetailsAsDates)

  const lastQuarterDetails = useReselector(
    selectQuarterDetailsForQuarterBeforeActiveQuarter
  )

  const lastTaxQuarter = `${lastQuarterDetails?.taxYear}-${lastQuarterDetails?.taxQuarter}`

  const calculation = useReselector(
    selectTaxCalculationByTaxQuarter,
    lastTaxQuarter
  )

  const homeState = useReselector(selectCalculationHomeState, calculation?.id)

  const lastQuarterIsQ4 = lastQuarterDetails?.taxQuarter === '4'

  const estimateIsNotPaidOrLate = useReselector(
    selectAreAnyEstimatesLastQuarterNotPaidOrPaidAfterIRSDeadline
  )

  const { federalTaxEstimate, homeStateTaxEstimate, stateTaxEstimates } =
    useReselector(getGroupedTaxEstimatesForQuarter, {
      taxQuarter: lastTaxQuarter,
      homeState,
    })

  return (
    <div className="space-y-6">
      <Text variant={TypographyStyles.H3}>Last Quarter&apos;s Estimates</Text>
      {estimateIsNotPaidOrLate && (
        <Text variant={TypographyStyles.BODY_MD}>
          {lastQuarterIsQ4
            ? `If you weren’t able to pay your Q4 estimate in time, we’ll account for this when preparing your annual tax return. Please note: this may increase your tax bill for ${lastQuarterDetails?.taxYear}. If you didn’t make a Q4 payment, you may incur an underpayment penalty fee.`
            : `If you weren’t able to submit your checklist and pay your Q${lastQuarterDetails?.taxQuarter} estimate in time, we’ll account for this when preparing your Q${activeQuarterDetails?.taxQuarter} estimate. Please note: you may see a larger Q${activeQuarterDetails?.taxQuarter} estimate as a result.`}
        </Text>
      )}
      <EstimatesForQuarter
        federalTaxEstimate={federalTaxEstimate}
        homeStateTaxEstimate={homeStateTaxEstimate}
        stateTaxEstimates={stateTaxEstimates}
      />
    </div>
  )
}

export const LastQuarterTaxSummary = () => {
  const previousQuarterSafeHarborAvailable = useReselector(
    selectPreviousQuarterSafeHarborAvailable
  )
  const activeQuarterDetails = useReselector(selectActiveQTEDetailsAsDates)

  if (!activeQuarterDetails) {
    return null
  }

  return (
    <div className="w-full space-y-6">
      <Alert customIconName={faCalendarCheck}>
        The Quarterly Checklist for Q{activeQuarterDetails?.taxQuarter}{' '}
        {activeQuarterDetails.taxYear} estimates will open on{' '}
        {activeQuarterDetails.forDisplay.checklistLaunchDate?.toFormat(
          DATE_FORMATS_LUXON.DISPLAY_LONG
        )}
      </Alert>
      {previousQuarterSafeHarborAvailable ? (
        <LastQuarterSafeHarborFlow />
      ) : (
        <LastQuarterNoSafeHarborContent />
      )}
    </div>
  )
}
